import { useEffect, useRef, useState } from "react";
import { Bot, X, SendHorizonal, Sparkles, Phone } from "lucide-react";

const GREETING = "Hi! Looking for help with a machined part or assembly? I can help you explore PMP’s capabilities and find the next step for your project.";
const PROMPTS = [
  "Explore machining capabilities",
  "Discuss prototype or production needs",
  "What should I include in my inquiry?",
  "Contact PMP",
];

const FALLBACK_LINKS = [
  { label: "Manufacturing capabilities", id: "capabilities" },
  { label: "Quality & verification", id: "quality" },
  { label: "Applications", id: "applications" },
  { label: "Project inquiry", id: "contact" },
];

// Live assistant (streaming) with a clearly-labeled guided help fallback.
export const AiAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [fallback, setFallback] = useState(false);
  const sessionRef = useRef(Math.random().toString(36).slice(2));
  const bodyRef = useRef(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    const history = messages.slice(1).slice(-8);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, history, sessionId: sessionRef.current }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let live = true;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.error) { live = false; break; }
          if (payload.t) setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + payload.t }; return c; });
        }
        if (!live) break;
      }
      if (!live) throw new Error("unavailable");
    } catch {
      setFallback(true);
      setMessages((m) => {
        const c = [...m];
        if (!c[c.length - 1].content) {
          c[c.length - 1] = { role: "assistant", content: "I can’t reach the live assistant right now. Here are the fastest paths:" };
        }
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="assistant-toggle"
        aria-label="Open the PMP Assistant"
        onClick={() => setOpen((v) => !v)}
        className="float-ctrl fixed bottom-[calc(0.9rem+env(safe-area-inset-bottom))] left-4 h-12 w-12 sm:left-6"
      >
        {open ? <X className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
      </button>

      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-[calc(4.4rem+env(safe-area-inset-bottom))] left-4 z-50 flex h-[30rem] max-h-[calc(100dvh-6.5rem)] w-[23rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-white/12 bg-[#0b0e13]/95 text-white shadow-2xl shadow-black/50 backdrop-blur-xl sm:left-6">
          <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--blue)]"><Bot className="h-4 w-4" /></span>
            <div className="flex-1">
              <p className="text-sm font-semibold">PMP Assistant</p>
              <p className="font-mono text-[9.5px] tracking-[0.18em] text-white/50">AUTOMATED GUIDANCE — NOT A QUOTE</p>
            </div>
          </div>

          <div ref={bodyRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m, i) => (
              <p key={i} data-testid={m.role === "user" ? "assistant-user-message" : "assistant-reply"} className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-[13.5px] leading-relaxed ${m.role === "user" ? "ml-auto bg-[var(--blue)] text-white" : "bg-white/8 border border-white/10 text-white/90"}`}>
                {m.content}
                {streaming && i === messages.length - 1 && m.role === "assistant" && <span className="pulse-dot ml-1 inline-block h-1.5 w-1.5 rounded-full bg-[var(--blue-soft)]" />}
              </p>
            ))}

            {messages.length === 1 && (
              <div className="flex flex-col gap-2 pt-2">
                {PROMPTS.map((pr) => (
                  <button key={pr} type="button" data-testid="assistant-prompt" onClick={() => send(pr)} className="flex items-center gap-2 rounded-full border border-white/12 bg-white/5 px-3.5 py-2 text-left text-[12.5px] text-white/85 transition-colors hover:bg-white/12">
                    <Sparkles className="h-3.5 w-3.5 shrink-0 text-[#FFC48C]" />
                    {pr}
                  </button>
                ))}
              </div>
            )}

            {fallback && (
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="flex flex-col gap-1.5">
                  {FALLBACK_LINKS.map((l) => (
                    <button key={l.id} type="button" onClick={() => { setOpen(false); const el = document.getElementById(l.id); if (el) (window.__lenis ? window.__lenis.scrollTo(el, { offset: -90 }) : el.scrollIntoView({ behavior: "smooth" })); }} className="rounded-lg px-2 py-1.5 text-left text-[13px] text-[#9db8ff] underline-offset-2 hover:underline">
                      {l.label} →
                    </button>
                  ))}
                  <a href="tel:+19704827676" className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-[#9db8ff] underline-offset-2 hover:underline">
                    <Phone className="h-3.5 w-3.5" /> Call (970) 482-7676
                  </a>
                </div>
              </div>
            )}
          </div>

          <form
            className="flex items-center gap-2 border-t border-white/10 p-3"
            onSubmit={(e) => { e.preventDefault(); send(); }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about capabilities, materials, or starting a project…"
              data-testid="assistant-input"
              className="min-w-0 flex-1 rounded-full border border-white/12 bg-white/5 px-4 py-2.5 text-[13px] text-white placeholder:text-white/40 focus:border-[var(--blue-soft)] focus:outline-none"
            />
            <button type="submit" data-testid="assistant-send" disabled={streaming} aria-label="Send message" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--blue)] transition-colors hover:bg-[#0033d6] disabled:opacity-50">
              <SendHorizonal className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
